<script setup lang="ts">
import type { IBestOfferCredit } from './types'
import { Button } from '~/components/ui/button'
import { Heading } from '~/components/ui/heading'
import { Rating } from '~/components/ui/rating'
import { UiCardTop } from '~/components/ui/card'
import { ANCHOR_IDS } from '~/lib/constants'

const { t } = useI18n()

const props = withDefaults(
  defineProps<{
    data: IBestOfferCredit
    highlighted?: boolean
    showTitle?: boolean
  }>(),
  {
    highlighted: false,
    showTitle: false
  }
)

// Build query object for credit link with filter parameters
const creditLinkQuery = computed(() => {
  const query: Record<string, string> = {}

  if (props.data.amount && props.data.amount > 0) {
    query.amount = String(props.data.amount)
  }

  if (props.data.currency && props.data.currency !== 'any') {
    query.currency = props.data.currency
  }

  if (props.data.period && props.data.period !== '0') {
    query.period = props.data.period
  }

  if (props.data.purpose && props.data.purpose !== '0') {
    query.purpose = props.data.purpose
  }

  return query
})
</script>

<template>
  <article
    :class="[props.highlighted ? 'CardPremium group' : 'Card border border-gray-200']"
    class="flex flex-col"
  >
    <UiCardTop v-if="props.highlighted" />
    <div class="flex flex-1 flex-col justify-between space-y-4">
      <!-- Title -->
      <div class="flex flex-col space-y-4">
        <Heading v-if="props.showTitle" tag="h3" class="h6">{{
          t('common.best_offer.title')
        }}</Heading>
        <!-- Gray block with bank info -->
        <div class="flex items-center gap-2 rounded-lg bg-gray-100 p-4">
          <NuxtLinkLocale
            :to="props.data.bank.url"
            :aria-label="props.data.bank.title"
            class="flex-shrink-0 rounded-lg border bg-white p-1.5"
          >
            <NuxtImg
              :src="props.data.bank.logo"
              width="40"
              height="40"
              class="min-w-5"
              :alt="props.data.bank.title"
            />
          </NuxtLinkLocale>
          <div class="flex w-full flex-col gap-0.5">
            <NuxtLinkLocale
              :to="props.data.bank.url"
              :aria-label="props.data.bank.title"
              class="text-sm hover:underline"
            >
              {{ props.data.bank.title }}
            </NuxtLinkLocale>
            <div class="mt-0.5 font-semibold">{{ props.data.title }}</div>
            <div v-if="props.data.bank.rating" class="flex items-center">
              <Rating :value="props.data.bank.rating" size="xs" />
              <div class="ml-2 text-sm text-gray-900">{{ props.data.bank.rating }}</div>
            </div>
          </div>
        </div>

        <!-- 4 columns: rate, monthly payment, overpayment, term -->
        <div class="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <div class="SecondaryTextHeadings">{{ t('credits.best_offer.rate') }}</div>
            <div class="font-semibold">{{ props.data.rate }}</div>
          </div>
          <div>
            <div class="SecondaryTextHeadings">{{ t('credits.best_offer.monthly_payment') }}</div>
            <div class="font-semibold">{{ props.data.monthlyPayment }}</div>
          </div>
          <div>
            <div class="SecondaryTextHeadings">{{ t('credits.best_offer.overpayment') }}</div>
            <div class="font-semibold">{{ props.data.overpayment }}</div>
          </div>
          <div>
            <div class="SecondaryTextHeadings">{{ t('common.best_offer.term') }}</div>
            <div class="font-semibold">{{ props.data.term }}</div>
          </div>
        </div>
      </div>

      <!-- Buttons -->
      <div class="flex flex-wrap gap-3">
        <Button as-child variant="blue">
          <NuxtLinkLocale
            :to="{
              path: props.data.url,
              query: creditLinkQuery,
              hash: `#${ANCHOR_IDS.FEATURE_CREDIT_FORM_REQUEST}`
            }"
          >
            {{ t('credits.best_offer.apply_credit') }}
          </NuxtLinkLocale>
        </Button>
      </div>
    </div>
  </article>
</template>
